/**
 * Google Analytics 4 loader + SPA page-view tracker.
 *
 * Privacy stance (healthcare-aware):
 *   - NO tracking on authenticated routes: /login, /signup, /dashboard,
 *     /verify-email, /complete-profile, /tools/boneah, /tools/nin-nutriguide.
 *   - Query strings are stripped before being sent (may contain tokens/ids).
 *   - IP anonymization enabled by default for GA4.
 *
 * Env:
 *   REACT_APP_GA_ID — GA4 measurement id (G-XXXXXXXXXX). If unset, this module
 *   is a no-op (safe for preview/dev without needing to set analytics up).
 */

const GA_ID = process.env.REACT_APP_GA_ID;

// Paths NOT to track — keeps medical/auth pages out of analytics entirely.
const BLOCKED_PREFIXES = [
  "/login",
  "/signup",
  "/dashboard",
  "/verify-email",
  "/complete-profile",
  "/tools/boneah",           // chooser page + legacy
  "/tools/bone-age",         // tool page (about page IS tracked)
  "/tools/growth",           // tool page (about page IS tracked)
  "/tools/nin-nutriguide",   // tool page (about page IS tracked)
];

function isTrackable(pathname) {
  // Allow the /about variants through (marketing pages), block the tool itself.
  for (const p of BLOCKED_PREFIXES) {
    if (pathname === p || (pathname.startsWith(p) && !pathname.startsWith(p + "/about"))) {
      return false;
    }
  }
  return true;
}

let injected = false;

export function initAnalytics() {
  if (!GA_ID || injected || typeof window === "undefined") return;
  injected = true;

  // Inject gtag.js
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // Don't auto-send a page_view; we do it manually on route change so SPA
  // navigation is tracked (and so we can apply the PHI filter).
  window.gtag("config", GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
}

export function trackPageView(pathname) {
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  if (!isTrackable(pathname)) return;
  window.gtag("event", "page_view", {
    page_path: pathname,
    page_location: window.location.origin + pathname,
    page_title: document.title,
  });
}
